import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, Inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { forkJoin, map, Observable } from 'rxjs';

import { formatPhoneNumber } from '../../../helpers';
import { DAYS_OF_WEEK } from '../../../shared';
import {
    ApiDataType,
    DocumentDTO,
    DocumentObjectType,
    EnvironmentInterface,
    GuardAddNewDocumentDTO,
    GuardApiInterface,
    PRO_ACCOUNT_STATUS,
    ProSchedule,
    ProScheduleFields,
    UserProApiDataInterface
} from '../../../types';

@Injectable({
    providedIn: 'root'
})
export class GuardService {
    user: WritableSignal<GuardApiInterface | null> = signal(null);
    userExists: Signal<boolean> = computed(() => !!this.user());
    isLoggedIn: Signal<boolean> = computed(() => !!this.user() && this.userExists());
    isVerified: Signal<boolean> = computed(
        () => !!this.user() && !!this.user()?.phone && !!this.user()?.phone_verified_at
    );
    isLead: Signal<boolean> = computed(() => !!this.user() && this.user()?.status === PRO_ACCOUNT_STATUS.LEAD);
    isApplicant: Signal<boolean> = computed(
        () => !!this.user() && this.user()?.status === PRO_ACCOUNT_STATUS.APPLICANT
    );
    isActive: Signal<boolean> = computed(() => !!this.user() && this.user()?.status === PRO_ACCOUNT_STATUS.ACTIVE);
    userSchedule: Signal<ProSchedule | null> = computed(() => this.user()?.schedule || null);
    userHasFullAvailability: Signal<boolean> = computed(() => {
        const schedule = this.user()?.schedule;
        const days = DAYS_OF_WEEK.map(day => day.value);

        return days.every(
            (day: string) =>
                schedule &&
                schedule?.[`${day}_morning` as ProScheduleFields] === 1 &&
                schedule?.[`${day}_evening` as ProScheduleFields] === 1 &&
                schedule?.[`${day}_overnight` as ProScheduleFields] === 1
        );
    });
    userHasNoAvailability: Signal<boolean> = computed(() => {
        const schedule = this.user()?.schedule;
        const days = DAYS_OF_WEEK.map(day => day.value);

        return days.every(
            (day: string) =>
                schedule &&
                schedule?.[`${day}_morning` as ProScheduleFields] === 0 &&
                schedule?.[`${day}_evening` as ProScheduleFields] === 0 &&
                schedule?.[`${day}_overnight` as ProScheduleFields] === 0
        );
    });
    formattedPhoneNumber: Signal<string | null> = computed(() => formatPhoneNumber(this.user()?.phone || '') as string);
    userDefaultAddressFull: Signal<string | null> = computed(() =>
        this.user()?.address
            ? `${this.user()?.address}, ${this.user()?.city}, ${this.user()?.province}, ${this.user()?.postal_code}`
            : null
    );
    userWorkAddressFull: Signal<string | null> = computed(() =>
        this.user()?.work_address
            ? `${this.user()?.work_address}, ${this.user()?.work_city}, ${this.user()?.work_province}, ${this.user()?.work_postal_code}`
            : null
    );

    userBirthdayValues: Signal<{
        year: string;
        month: string;
        day: string;
    }> = computed(() => {
        const birthdaySplit = this.user()?.birthday?.split('-') || '';
        return {
            year: birthdaySplit?.[0] || '',
            month: birthdaySplit?.[1] || '',
            day: birthdaySplit?.[2] || ''
        };
    });

    /* eslint-disable @typescript-eslint/no-explicit-any */
    private environment: EnvironmentInterface;

    constructor(
        private http: HttpClient,
        @Inject('environment') environment: EnvironmentInterface
    ) {
        this.environment = environment;
    }

    setUser(user: GuardApiInterface | null) {
        this.user.set(user);
    }

    setUserFieldValues(updatedValues: Partial<GuardApiInterface> = {}) {
        const user = this.user();
        if (!user) return;
        const updatedUser = {
            ...user,
            ...updatedValues
        };
        this.user.set(updatedUser);
    }

    getUserWithToken(token: string): Observable<UserProApiDataInterface> {
        const header = new HttpHeaders({
            Authorization: token
        });
        return this.http
            .get<UserProApiDataInterface>(`${this.environment.apiUrl}/account`, {
                headers: header
            })
            .pipe(
                map(data => {
                    console.log(data.data);
                    this.user.set(data.data);
                    return data;
                })
            );
    }

    getUpdatedUser(automaticallySetUser: boolean = true) {
        return this.http.get<UserProApiDataInterface>(`${this.environment.apiUrl}/account`).pipe(
            map(data => {
                if (automaticallySetUser) {
                    console.log('set user');
                    console.log(data?.data);
                    this.user.set(data.data);
                }
                return data;
            })
        );
    }

    updateAccount(
        data: {
            password?: string;
            current_password?: string;
            password_confirmation?: string;
            travel_radius?: number;
            ignore_travel_radius?: boolean;
            min_rate_preference?: number | null;
            max_rate_preference?: number | null;
            notify_on_new_jobs?: boolean;
            notify_on_system_updates?: boolean;
            photo?: string;
            address?: string | null;
            unit_number?: string | null;
            city?: string | null;
            province?: string | null;
            postal_code?: string | null;
            work_address?: string | null;
            work_unit_number?: string | null;
            work_city?: string | null;
            work_province?: string | null;
            work_postal_code?: string | null;
            bio?: string | null;
        },
        autoUpdateUserData: boolean = false
    ): Observable<UserProApiDataInterface> {
        return this.http.post<UserProApiDataInterface>(`${this.environment.apiUrl}/account`, data).pipe(
            map(data => {
                if (autoUpdateUserData) {
                    this.setUserFieldValues(data?.data);
                }
                return data;
            })
        );
    }

    updateAvailability(obj: { [K in ProScheduleFields]: 0 | 1 }): Observable<any> {
        return this.http.post(`${this.environment.apiUrl}/account/availability`, obj);
    }

    //Pros are created with their schedules as `null`
    //This will create a default schedule for their account
    addFullAvailabilitySchedule(): Observable<any> {
        const fullAvailability = {
            sunday_morning: true,
            sunday_overnight: true,
            sunday_evening: true,
            monday_morning: true,
            monday_overnight: true,
            monday_evening: true,
            tuesday_morning: true,
            tuesday_overnight: true,
            tuesday_evening: true,
            wednesday_morning: true,
            wednesday_overnight: true,
            wednesday_evening: true,
            thursday_morning: true,
            thursday_overnight: true,
            thursday_evening: true,
            friday_morning: true,
            friday_overnight: true,
            friday_evening: true,
            saturday_morning: true,
            saturday_overnight: true,
            saturday_evening: true
        };
        return this.http.post(`${this.environment.apiUrl}/account/availability`, fullAvailability);
    }

    addDocumentFiles(files: DocumentObjectType[]): Observable<GuardAddNewDocumentDTO[]> {
        return forkJoin(
            files.map(doc =>
                this.addDocumentFile({
                    file: doc.base64,
                    expiration_month: doc.expiration_month,
                    expiration_year: doc.expiration_year,
                    type: doc.type || 'document',
                    name: doc.name
                })
            )
        );
    }

    addDocumentFile(file: DocumentDTO): Observable<GuardAddNewDocumentDTO> {
        return this.http.post<GuardAddNewDocumentDTO>(`${this.environment.apiUrl}/document/add`, file);
    }

    removeDocumentFile(id: number): Observable<ApiDataType> {
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}/document/${id}/remove`, {});
    }

    contact(data: any): Observable<ApiDataType> {
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}/contact`, data);
    }

    deleteAccount(): Observable<ApiDataType> {
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}/account/delete`, {});
    }
}
